// this component was automatically generated by IconGlyph.template.js
import type { FunctionComponent } from 'react';
import { Suspense } from 'react';

import {
  ChunkLoadErrorBoundary,
  ErrorBoundary,
} from '@trello/error-boundaries';
import { useFeatureGate } from '@trello/feature-gate-client';
import type { TestId } from '@trello/test-ids';
import type {
  IconColor,
  IconColorPressed,
  TextColor,
  TextColorPressed,
} from '@trello/theme';
import { useLazyComponent } from '@trello/use-lazy-component';

import type { IconSize } from '../src/components/Icon';
import { Icon } from '../src/components/Icon';

type ADSIconType =
  | Exclude<TextColor, 'transparent'>
  | IconColor
  | IconColorPressed
  | TextColorPressed
  | 'currentColor';

export interface GlyphProps {
  /**
   * A string that is applied as an aria attribute on the icon.
   */
  label?: string;
  /**
   * The color that the Icon should be rendered as.
   * @default token('color.icon', '#42526E')
   */
  color?: ADSIconType | string;
  /**
   * The size to render the Icon size.
   * @default "medium"
   */
  size?: IconSize;
  /**
   * A string that gets placed as a data attribute (data-testid) onto the
   * Icon wrapper so that our
   * smoketest can properly target and test the component
   * @default undefined
   */
  testId?: TestId | string;
  // Escape hatches
  /**
   * ⚠️ DO NOT USE THIS PROP UNLESS YOU REALLY REALLY HAVE TO.
   *
   * Places a class name on the Icon (more specifically, the svg itself). This
   * is placed in addition to the classes already placed on the Icon. This is
   * placed directly onto the SVG via the glyph templates that are
   * generated by IconGlyph.template.js
   *
   * Please refrain from using this unless absolutely necessary.
   * @default undefined
   */
  dangerous_className?: string;
  /**
   * The switch for the icon to be centered in the dedicated space with padding around it.
   * Designed for cases when icon is not inline.
   */
  block?: boolean;
}

const PinIconGlyph = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24px"
      height="24px"
      role="presentation"
      focusable="false"
      viewBox="0 0 24 24"
    >
      <path
        transform="rotate(45, 12, 12)"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="currentColor"
        d="M 6.375 3.117188 C 5.9375 3.351562 5.90625 3.480469 5.90625 5.0625 C 5.90625 7.039062 5.96875 7.121094 7.421875 7.125 L 8.1875 7.125 L 8.1875 10.859375 L 7.484375 11.851562 L 6.785156 12.839844 L 6.761719 13.472656 C 6.707031 14.871094 6.820312 14.933594 9.421875 14.9375 L 11.3125 14.9375 L 11.3125 17.691406 C 11.3125 20.878906 11.335938 21 12 21 C 12.664062 21 12.6875 20.878906 12.6875 17.691406 L 12.6875 14.9375 L 14.503906 14.9375 C 17.003906 14.9375 17.019531 14.929688 17.050781 13.632812 L 17.070312 12.839844 L 16.4375 11.945312 C 15.566406 10.722656 15.625 10.9375 15.625 8.878906 L 15.625 7.125 L 16.421875 7.125 C 17.886719 7.125 17.949219 7.035156 17.925781 4.992188 L 17.90625 3.511719 L 17.75 3.339844 C 17.425781 2.988281 17.632812 3 11.890625 3 C 6.839844 3 6.582031 3.003906 6.375 3.117188 "
      />
    </svg>
  );
};

export const PinIcon: FunctionComponent<GlyphProps> = (props) => {
  const { testId, dangerous_className, size, color, label, block } = props;
  const { value: isIconFlagEnabled } = useFeatureGate('trello_ads_icons');

  const LazyAdsIcon = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "ADS-PinIcon" */ '@atlaskit/icon/core/pin-filled'
      ),
    { namedImport: 'default', preload: false },
  );

  const adsIconSize = size === 'medium' || size === 'small' ? size : undefined;

  return isIconFlagEnabled ? (
    <span className={dangerous_className}>
      <ErrorBoundary
        tags={{
          ownershipArea: 'trello-ghost',
          feature: 'ADS Icon',
        }}
      >
        <ChunkLoadErrorBoundary fallback={null}>
          <Suspense fallback={null}>
            <LazyAdsIcon
              label={label || ''}
              testId={testId || 'PinIcon'}
              color={color as ADSIconType}
              size={adsIconSize}
            />
          </Suspense>
        </ChunkLoadErrorBoundary>
      </ErrorBoundary>
    </span>
  ) : (
    <Icon
      testId={testId || 'PinIcon'}
      size={size}
      dangerous_className={dangerous_className}
      color={color}
      block={block}
      label={label}
      glyph={PinIconGlyph}
    />
  );
};
